import { useMemo, useState } from "react";
import { X, CheckCircle2, Link2, Plus, AlertTriangle, Network, ArrowRight } from "lucide-react";

/**
 * EcosystemDeployModal — P1 2026-06 : validation humaine d'une grappe ontologique.
 * Doctrine : la validation matérialise les objets mais ne change JAMAIS leur
 * statut épistémique. Résolution d'identité pré-calculée côté serveur :
 *  - existing  → "Existante · sera reliée" (décochable, pas de doublon proposé)
 *  - ambiguous → arbitrage humain obligatoire (relier à X / créer nouvelle)
 *  - new       → "Sera créée"
 */

const EPI = {
  declared: { label: "Déclaré", color: "#1D9E75" },
  inferred: { label: "Inféré", color: "#EF9F27" },
  source: { label: "Source", color: "#378ADD" },
};

export function EpiBadge({ value }) {
  const e = EPI[value] || EPI.inferred;
  return (
    <span
      className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full border shrink-0"
      style={{ color: e.color, borderColor: `${e.color}4d`, background: `${e.color}1a` }}
      data-testid={`epi-badge-${value}`}
    >
      {e.label}
    </span>
  );
}

function StatusBadge({ status }) {
  if (status === "existing") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#378ADD]/15 text-[#378ADD] border border-[#378ADD]/30 shrink-0">
        <Link2 className="w-2.5 h-2.5" /> Existante · sera reliée
      </span>
    );
  }
  if (status === "ambiguous") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/30 shrink-0">
        <AlertTriangle className="w-2.5 h-2.5" /> À arbitrer
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 shrink-0">
      <Plus className="w-2.5 h-2.5" /> Sera créée
    </span>
  );
}

export default function EcosystemDeployModal({ action, busy, onCancel, onConfirm }) {
  const [entities, setEntities] = useState(() =>
    (action.entities || []).map((e) => {
      const res = e.resolution || { status: "new" };
      return {
        ...e,
        _checked: true,
        _decision: res.status === "existing" ? "link" : res.status === "new" ? "create" : null,
        _existingId: res.status === "existing" ? res.existing?.id || null : null,
      };
    })
  );
  const [items, setItems] = useState(() => (action.items || []).map((it) => ({ ...it, _checked: true })));
  const [rels, setRels] = useState(() => (action.relationships || []).map((r) => ({ ...r, _checked: true })));

  const entMap = useMemo(() => Object.fromEntries(entities.map((e) => [e.temp_id, e])), [entities]);
  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.temp_id, i])), [items]);

  const labelOf = (tid) => {
    const e = entMap[tid];
    if (e) return e.prefill?.name || tid;
    const it = itemMap[tid];
    return it ? it.prefill?.title || it.prefill?.titre || it.prefill?.nom || it.module : tid;
  };
  const endpointReady = (tid) => {
    const e = entMap[tid];
    if (e) return e._checked && !!e._decision;
    const it = itemMap[tid];
    return it ? it._checked : false;
  };

  const setEntity = (tid, patch) =>
    setEntities((arr) => arr.map((e) => (e.temp_id === tid ? { ...e, ...patch } : e)));

  const pendingAmbiguous = entities.filter((e) => e._checked && !e._decision).length;
  const activeRels = rels.filter((r) => r._checked && endpointReady(r.from) && endpointReady(r.to));
  const selCount = entities.filter((e) => e._checked).length + items.filter((i) => i._checked).length;

  const handleConfirm = () => {
    onConfirm({
      entities: entities
        .filter((e) => e._checked && e._decision)
        .map((e) => ({
          temp_id: e.temp_id,
          prefill: e.prefill,
          epistemic: e.epistemic,
          evidence: e.evidence,
          decision: e._decision,
          existing_id: e._decision === "link" ? e._existingId : null,
        })),
      items: items
        .filter((i) => i._checked)
        .map((i) => ({ temp_id: i.temp_id, module: i.module, prefill: i.prefill, epistemic: i.epistemic, evidence: i.evidence })),
      relationships: activeRels.map((r) => ({
        from_temp: r.from,
        to_temp: r.to,
        rel_type: r.rel_type,
        rel_label: r.rel_label,
        epistemic: r.epistemic,
        evidence: r.evidence,
      })),
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4"
      onClick={() => !busy && onCancel()}
      data-testid="ecosystem-deploy-overlay"
    >
      <div
        className="noam-card w-full max-w-3xl p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="ecosystem-deploy-modal"
      >
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="noam-overline text-[#7F77DD] inline-flex items-center gap-1.5">
              <Network className="w-3.5 h-3.5" /> Grappe d'écosystème
            </div>
            <div className="text-base font-semibold text-zinc-100 mt-1">
              {action.label || "Déployer l'écosystème proposé"}
            </div>
            {action.reason && <div className="text-[11px] text-zinc-300 mt-1">{action.reason}</div>}
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="p-1 rounded hover:bg-white/5 text-zinc-400 disabled:opacity-50"
            data-testid="ecosystem-deploy-cancel-x"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="text-[10px] text-zinc-500 mb-4">
          La validation matérialise les objets sans changer leur statut épistémique : ce qui est inféré reste marqué inféré.
        </div>

        {/* ===== Entités ===== */}
        {entities.length > 0 && (
          <section className="mb-4">
            <div className="text-[11px] text-zinc-400 uppercase tracking-wider mb-2">
              Entités ({entities.length})
            </div>
            <ul className="space-y-2" data-testid="ecosystem-entities-list">
              {entities.map((e) => {
                const res = e.resolution || { status: "new" };
                return (
                  <li
                    key={e.temp_id}
                    className={`px-3 py-2.5 rounded-md border ${
                      e._checked ? "bg-white/[0.03] border-white/15" : "bg-transparent border-white/5 opacity-50"
                    }`}
                    data-testid={`ecosystem-entity-${e.temp_id}`}
                  >
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={e._checked}
                        onChange={() => setEntity(e.temp_id, { _checked: !e._checked })}
                        disabled={busy}
                        className="mt-1 w-4 h-4 rounded border-white/20 bg-zinc-900 text-[#7F77DD] cursor-pointer"
                        data-testid={`ecosystem-entity-checkbox-${e.temp_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-zinc-100 font-medium">{e.prefill?.name}</span>
                          {e.prefill?.type && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-700/50 text-zinc-300">{e.prefill.type}</span>
                          )}
                          <EpiBadge value={e.epistemic} />
                          <StatusBadge status={res.status} />
                        </div>
                        {res.status === "existing" && res.existing && (
                          <div className="text-[10px] text-[#378ADD] mt-1">
                            → reliée à « {res.existing.name} » · méthode : {res.method}
                            {(res.evidence || []).length > 0 && ` · ${res.evidence.join(" · ")}`}
                          </div>
                        )}
                        {e.evidence && <div className="text-[10px] text-zinc-500 mt-0.5 italic">{e.evidence}</div>}

                        {/* Arbitrage humain pour les ambigus */}
                        {res.status === "ambiguous" && e._checked && (
                          <div className="mt-2 space-y-1" data-testid={`ecosystem-entity-arbitrage-${e.temp_id}`}>
                            {(res.candidates || []).map((c) => (
                              <label key={c.id} className="flex items-center gap-2 text-[11px] text-zinc-200 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`arb-${e.temp_id}`}
                                  checked={e._decision === "link" && e._existingId === c.id}
                                  onChange={() => setEntity(e.temp_id, { _decision: "link", _existingId: c.id })}
                                  disabled={busy}
                                  className="w-3.5 h-3.5"
                                  data-testid={`ecosystem-arb-link-${e.temp_id}-${c.id}`}
                                />
                                <Link2 className="w-3 h-3 text-[#378ADD]" />
                                Relier à « {c.name} »
                                <span className="text-zinc-500">({[c.type, c.country, c.sector].filter(Boolean).join(" · ")} — {c.motive})</span>
                              </label>
                            ))}
                            <label className="flex items-center gap-2 text-[11px] text-zinc-200 cursor-pointer">
                              <input
                                type="radio"
                                name={`arb-${e.temp_id}`}
                                checked={e._decision === "create"}
                                onChange={() => setEntity(e.temp_id, { _decision: "create", _existingId: null })}
                                disabled={busy}
                                className="w-3.5 h-3.5"
                                data-testid={`ecosystem-arb-create-${e.temp_id}`}
                              />
                              <Plus className="w-3 h-3 text-emerald-400" />
                              Créer une nouvelle entité distincte
                            </label>
                          </div>
                        )}
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* ===== Objets métier ===== */}
        {items.length > 0 && (
          <section className="mb-4">
            <div className="text-[11px] text-zinc-400 uppercase tracking-wider mb-2">
              Objets ({items.length})
            </div>
            <ul className="space-y-2" data-testid="ecosystem-items-list">
              {items.map((it) => (
                <li
                  key={it.temp_id}
                  className={`px-3 py-2.5 rounded-md border ${
                    it._checked ? "bg-white/[0.03] border-white/15" : "bg-transparent border-white/5 opacity-50"
                  }`}
                  data-testid={`ecosystem-item-${it.temp_id}`}
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={it._checked}
                      onChange={() =>
                        setItems((arr) => arr.map((x) => (x.temp_id === it.temp_id ? { ...x, _checked: !x._checked } : x)))
                      }
                      disabled={busy}
                      className="mt-1 w-4 h-4 rounded border-white/20 bg-zinc-900 text-emerald-500 cursor-pointer"
                      data-testid={`ecosystem-item-checkbox-${it.temp_id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">
                          {it.module_label || it.module}
                        </span>
                        <span className="text-sm text-zinc-100 font-medium">
                          {it.prefill?.title || it.prefill?.titre || it.prefill?.nom || it.prefill?.name || ""}
                        </span>
                        <EpiBadge value={it.epistemic} />
                        <StatusBadge status="new" />
                      </div>
                      {it.evidence && <div className="text-[10px] text-zinc-500 mt-0.5 italic">{it.evidence}</div>}
                    </div>
                  </label>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ===== Relations ===== */}
        {rels.length > 0 && (
          <section className="mb-4">
            <div className="text-[11px] text-zinc-400 uppercase tracking-wider mb-2">
              Relations ({rels.length})
            </div>
            <ul className="space-y-1.5" data-testid="ecosystem-rels-list">
              {rels.map((r, idx) => {
                const ready = endpointReady(r.from) && endpointReady(r.to);
                return (
                  <li
                    key={idx}
                    className={`px-3 py-2 rounded-md border flex items-center gap-2 flex-wrap ${
                      r._checked && ready ? "bg-white/[0.03] border-white/15" : "bg-transparent border-white/5 opacity-50"
                    }`}
                    data-testid={`ecosystem-rel-${idx}`}
                  >
                    <input
                      type="checkbox"
                      checked={r._checked}
                      onChange={() => setRels((arr) => arr.map((x, i) => (i === idx ? { ...x, _checked: !x._checked } : x)))}
                      disabled={busy || !ready}
                      className="w-3.5 h-3.5 rounded border-white/20 bg-zinc-900 cursor-pointer"
                      data-testid={`ecosystem-rel-checkbox-${idx}`}
                    />
                    <span className="text-[12px] text-zinc-100">{labelOf(r.from)}</span>
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#7F77DD]/15 text-[#B4AFF0] border border-[#7F77DD]/30">
                      {r.rel_label || r.rel_type} <ArrowRight className="w-2.5 h-2.5" />
                    </span>
                    <span className="text-[12px] text-zinc-100">{labelOf(r.to)}</span>
                    <EpiBadge value={r.epistemic} />
                    {!ready && <span className="text-[10px] text-zinc-500">extrémité non retenue</span>}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <div className="flex items-center justify-between gap-2 pt-3 border-t border-white/5">
          <div className="text-[11px] text-zinc-400">
            {pendingAmbiguous > 0 ? (
              <span className="text-[#EF9F27]">
                {pendingAmbiguous} identité(s) à arbitrer avant de déployer
              </span>
            ) : (
              <span>
                <span className="text-[#7F77DD] font-medium">{selCount}</span> objet(s) ·{" "}
                <span className="text-[#7F77DD] font-medium">{activeRels.length}</span> relation(s) seront matérialisés
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="text-[12px] px-3 py-1.5 rounded-md border border-white/10 text-zinc-300 hover:bg-white/5 disabled:opacity-50"
              data-testid="ecosystem-deploy-cancel-btn"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy || pendingAmbiguous > 0 || selCount === 0}
              className="text-[12px] px-3 py-1.5 rounded-md bg-[#7F77DD] text-white hover:bg-[#8F88E5] font-medium inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="ecosystem-deploy-confirm-btn"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              {busy ? "Déploiement…" : "Déployer la grappe"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
